import { useLang } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { ArrowRight, CreditCard, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import DemoDialog from "./DemoDialog";
import { track, REGISTER_URL } from "@/lib/analytics";
import { useIntent } from "@/intent/IntentContext";

const toneClass: Record<string, string> = {
  mint: "bg-mint",
  butter: "bg-butter",
  coral: "bg-coral",
  lavender: "bg-lavender",
};

const Hero = () => {
  const { t, lang } = useLang();
  const { audience } = useIntent();
  const m = t.hero_mock;
  const isEn = lang === "en";

  const intentCta = audience
    ? audience === "broker"
      ? { label: isEn ? "See agent solution" : "Bekijk voor makelaars", path: isEn ? "/en/for-agents" : "/voor-makelaars" }
      : audience === "vendor"
      ? { label: isEn ? "See vendor solution" : "Bekijk voor leveranciers", path: isEn ? "/en/for-vendors" : "/voor-leveranciers" }
      : { label: isEn ? "Meet the Home Advisor" : "Ontdek de Wooncoach", path: isEn ? "/en/home-advisor" : "/wooncoach" }
    : null;
  return (
    <section id="top" className="relative pt-28 pb-16 md:pt-36 md:pb-24 bg-hero overflow-hidden">
      <div className="container relative grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-mint text-mint-foreground px-3 py-1 text-xs font-bold uppercase tracking-wider">
            <span className="h-1.5 w-1.5 rounded-full bg-mint-foreground" />
            {t.hero.badge}
          </span>
          <h1 className="mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] text-foreground break-words">
            {t.hero.title}{" "}
            <span className="relative inline-block">
              <span className="relative z-10">{t.hero.titleAccent}</span>
              <span className="absolute left-0 right-0 bottom-1 h-3 md:h-4 bg-coral/80 -z-0 rounded-sm" />
            </span>
            .
          </h1>
          <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed">{t.hero.subtitle}</p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <a
              href={REGISTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("register_click", "hero")}
            >
              <Button size="lg" className="rounded-full h-12 px-7 text-base w-full sm:w-auto">
                {t.hero.cta1} <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </a>
            {intentCta ? (
              <Link to={intentCta.path} onClick={() => track("nav_click", `hero_intent_${audience}`)}>
                <Button size="lg" variant="outline" className="rounded-full h-12 px-7 text-base">
                  {intentCta.label}
                </Button>
              </Link>
            ) : (
              <DemoDialog
                trigger={
                  <Button size="lg" variant="outline" className="rounded-full h-12 px-7 text-base">
                    {t.hero.cta2}
                  </Button>
                }
              />
            )}
          </div>
        </div>

        <div className="relative h-[460px] md:h-[520px]" role="img" aria-label={`${m.shopName} — ${m.aiLabel}`}>
          {/* Soft color blobs that bleed through the glass */}
          <div aria-hidden="true" className="absolute -top-6 -right-6 h-56 w-56 rounded-full bg-coral/40 blur-3xl" />
          <div aria-hidden="true" className="absolute bottom-0 -left-10 h-56 w-56 rounded-full bg-lavender/50 blur-3xl" />

          <article aria-label={`Marketplace ${m.shopName}`} className="absolute top-0 right-0 w-[320px] md:w-[380px] rounded-3xl glass shadow-elegant overflow-hidden rotate-2">
            <div className="h-2 bg-gradient-primary" />
            <div className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-muted-foreground">{m.url}</div>
                  <div className="text-base font-bold mt-0.5">{m.shopName}</div>
                </div>
                <div className="h-8 w-8 rounded-full bg-gradient-primary" aria-hidden="true" />
              </div>
              <ul className="mt-4 grid grid-cols-2 gap-3">
                {m.items.map((it) => (
                  <li key={it.name} className={`rounded-2xl p-3 ${toneClass[it.tone] || "bg-secondary"}`}>
                    <div className="h-12 rounded-lg bg-background/40" aria-hidden="true" />
                    <div className="mt-2 text-xs font-bold">{it.name}</div>
                    <div className="text-xs opacity-70">{it.price}</div>
                  </li>
                ))}
              </ul>
            </div>
          </article>

          <aside aria-label={`${m.payoutLabel}: ${m.payoutAmount}`} className="absolute bottom-12 left-0 md:left-4 w-[260px] rounded-2xl glass shadow-elegant p-4 -rotate-3">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-xl bg-mint flex items-center justify-center" aria-hidden="true">
                <CreditCard className="h-4 w-4 text-mint-foreground" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">{m.payoutLabel}</div>
                <div className="text-base font-bold">{m.payoutAmount}</div>
              </div>
            </div>
            <div className="mt-3 h-1.5 rounded-full bg-secondary overflow-hidden" aria-hidden="true">
              <div className="h-full w-3/4 bg-gradient-primary rounded-full" />
            </div>
          </aside>

          <aside aria-label={`${m.aiLabel}: ${m.aiSnippet}`} className="absolute top-32 left-0 md:left-2 w-[240px] rounded-2xl glass-dark text-primary-foreground shadow-elegant p-4 rotate-[-2deg]">
            <div className="flex items-center gap-2 text-xs opacity-90">
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> {m.aiLabel}
            </div>
            <div className="mt-2 text-sm leading-snug">"{m.aiSnippet}"</div>
          </aside>

          <div className="hidden md:flex absolute bottom-0 right-8 items-center gap-2 rounded-full glass shadow-card px-4 py-2 rotate-2" aria-label={m.pill}>
            <span className="text-xs font-bold">{m.pill}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
